<template>
    <v-card class="pa-2">
        <v-card-title class="headline px-3"
            >{{ payment ? "Update" : "New" }} Payment</v-card-title
        >
        <div class="pa-3">
            <v-text-field
                class="mb-2"
                type="number"
                label="Amount"
                ref="amount"
                prefix="$"
                v-model="amount"
                :rules="required"
                :disabled="isCreditCardPayment()"
                @input="amountFound = amount"
            ></v-text-field>
            <v-text-field
                v-if="paymentType != 'Credit Card'"
                class="mb-2"
                type="number"
                label="Amount Found"
                prefix="$"
                ref="amountFound"
                v-model="amountFound"
                :rules="amountFoundRules"
            ></v-text-field>
            <v-select
                class="mb-4"
                :items="paymentTypes"
                label="Payment Type"
                v-model="paymentType"
                :disabled="isCreditCardPayment()"
                :rules="required"
                ref="paymentType"
                hide-details
            ></v-select>

            <div v-if="paymentType == 'Credit Card' && !payment">
                <v-text-field
                    class=""
                    label="Card Number"
                    ref="cardNumber"
                    :mask="creditCardMask"
                    v-model="cardNumber"
                    :rules="cardNumberRules"
                    append-icon="mdi-credit-card"
                ></v-text-field>
                <v-layout row class="mb-2">
                    <v-text-field
                        class="mr-2"
                        style="max-width: 25%;"
                        label="Exp. Date"
                        placeholder="MM/YY"
                        ref="expDate"
                        :mask="expDateMask"
                        :rules="expDateRules"
                        v-model="expDate"
                    ></v-text-field>
                    <v-text-field
                        style="margin-bottom: -27px; max-width: 28%;"
                        label="Security Code"
                        ref="securityCode"
                        v-model="securityCode"
                        :rules="securityCodeRules"
                        :mask="securityCodeMask"
                    ></v-text-field>
                    <div style="height: 48px; max-width: 45%">
                        <img
                            src="https://utaw.blob.core.windows.net/images/credit-card-cvc-all.svg"
                            style="width: 77px;"
                        />
                        <img
                            src="https://utaw.blob.core.windows.net/images/credit-card-cvc-amex.svg"
                            style="width: 77px;"
                        />
                    </div>
                </v-layout>
            </div>

            <v-menu
                v-model="datePickerMenu"
                :close-on-content-click="true"
                offset-y
                full-width
            >
                <template v-slot:activator="{ on }">
                    <v-text-field
                        class="mb-4"
                        v-model="date"
                        :value="formattedDate"
                        label="Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        hide-details
                        v-on="on"
                    ></v-text-field>
                </template>
                <v-date-picker v-model="date"></v-date-picker>
            </v-menu>
            <v-combobox
                class="mb-2"
                :items="noteOptions"
                v-model="note"
                label="Note"
            ></v-combobox>

            <v-checkbox
                class="my-3"
                hide-details
                label="Confirmed"
                color="success"
                v-model="confirmed"
            ></v-checkbox>
        </div>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn dark color="default" @click="closeDialog()">Cancel</v-btn>
            <v-btn :loading="processing" color="success" @click="save()">
                Save Payment
            </v-btn>
        </v-card-actions>
    </v-card>
</template>
<script>
import moment from "moment";
import { isCharityCard, toISOLocal } from "../../utils/helpers.js";
import cardValidator from "card-validator";

export default {
    props: {
        student: Object,
        reload: Function,
        closeNewPaymentDialog: Function,
        getDomain: Function,
        payment: Object,
        setup: Object
    },
    components: {},
    data() {
        return {
            processing: false,
            amount: "",
            amountFound: "",
            confirmed: true,
            note: "",
            paymentType: "Cash",
            paymentTypes: ["Cash", "Check", "Credit", "Credit Card"],
            datePickerMenu: false,
            date: toISOLocal(new Date(moment())).substr(0, 10),

            noteOptions: [
                "In office for Y.B.",
                "In office for A.H.M.",
                "Money not found",
                "By the menahl"
            ],

            required: [v => !!v || "Amount Is Required"],
            amountFoundRules: [
                v => !!v || v === 0 || "Required",
                v =>
                    !v || v <= +this.amount || "Must be less or equal to amount"
            ],

            cardNumber: "",
            securityCode: "",
            expDate: "",
            expDateMask: "##/##",

            cardNumberRules: [
                v => this.selectedCard || !!v || "Card Number Is Required",
                v => (v && this.validateCard()) || "Invalid Card Number"
            ],
            expDateRules: [
                v => !!v || "Required",
                v =>
                    (v && this.validateExpDate(v)) ||
                    "A valid 4 digit expiration date required"
            ],
            securityCodeRules: [
                v => this.selectedCard || !!v || "Security Code Is Required",
                v =>
                    (v && this.validateSecurityCode()) ||
                    "Invalid Security Code"
            ]
        };
    },
    async created() {},
    watch: {
        payment(newValue, oldValue) {
            if (newValue) {
                this.amount = newValue.amount;
                this.amountFound = newValue.amount - newValue.missingAmount;
                this.paymentType = newValue.paymentType;
                this.note = newValue.note;
                this.confirmed = newValue.confirmed;
                this.date = toISOLocal(new Date(moment(newValue.date))).substr(
                    0,
                    10
                );
            }
        }
    },
    computed: {
        formattedDate() {
            return this.date ? moment(this.date).format("L") : "";
        },
        creditCardMask() {
            const numberValidation = cardValidator.number(this.cardNumber);

            return numberValidation.card &&
                numberValidation.card.type == "american-express"
                ? "#### ###### #####"
                : "#### #### #### ####";
        },
        securityCodeMask() {
            const numberValidation = cardValidator.number(this.cardNumber);

            return numberValidation.card && numberValidation.card.code.size == 4
                ? "####"
                : "###";
        }
    },
    methods: {
        closeDialog() {
            this.$refs.amount.resetValidation();
            this.$refs.paymentType.resetValidation();

            if (!this.payment && this.paymentType == "Credit Card") {
                this.$refs.cardNumber.resetValidation();
                this.$refs.expDate.resetValidation();
                this.$refs.securityCode.resetValidation();
            } else if (this.paymentType != "Credit Card") {
                this.$refs.amountFound.resetValidation();
            }

            this.amount = "";
            this.amountFound = "";
            this.paymentType = "Cash";
            this.confirmed = true;
            this.note = "";
            this.date = toISOLocal(new Date(moment())).substr(0, 10);

            this.closeNewPaymentDialog();
        },
        isCreditCardPayment() {
            return this.payment && this.paymentType == "Credit Card";
        },
        validateCard() {
            const numberValidation = cardValidator.number(this.cardNumber);

            return numberValidation.isValid || isCharityCard(this.cardNumber);
        },
        validateSecurityCode() {
            const numberValidation = cardValidator.number(this.cardNumber);

            if (
                numberValidation &&
                numberValidation.card &&
                numberValidation.card.code
            ) {
                return cardValidator.cvv(
                    this.securityCode,
                    numberValidation.card.code.size
                ).isValid;
            }

            return cardValidator.cvv(this.securityCode).isValid;
        },
        validateExpDate(expDate) {
            const date = expDate.replace("/", "");

            if (date.length > 4) return false;

            return cardValidator.expirationDate(expDate).isValid;
        },
        getMissingAmount() {
            return this.amount - this.amountFound;
        },
        validate() {
            if (this.paymentType == "Credit Card" && !this.payment) {
                return [
                    this.$refs.amount.validate(true),
                    this.$refs.paymentType.validate(true),
                    this.$refs.cardNumber.validate(true),
                    this.$refs.expDate.validate(true),
                    this.$refs.securityCode.validate(true)
                ].every(Boolean);
            }

            const validations = [
                this.$refs.amount.validate(true),
                this.$refs.paymentType.validate(true)
            ];

            if (this.paymentType != "Credit Card") {
                validations.push(this.$refs.amountFound.validate(true));
            }

            return validations.every(Boolean);
        },
        async save() {
            if (!this.validate()) return;

            const isUpdating = this.payment;

            this.processing = true;

            const today = moment().format("YYYY-MM-DD HH:mm:ss");

            if (this.paymentType == "Credit Card" && !this.payment) {
                try {
                    await this.processCreditCard();
                } catch (e) {
                    this.processing = false;

                    return;
                }
            }

            try {
                await this.$http.post(
                    this.getDomain() + "/api/transactions/post",
                    {
                        transactionID: isUpdating
                            ? this.payment.transactionID
                            : 0,
                        year: this.setup.currentYear,
                        date: moment(this.date).format("YYYY-MM-DD HH:mm:ss"),
                        enteredOn: isUpdating
                            ? moment(this.payment.date).format(
                                  "YYYY-MM-DD HH:mm:ss"
                              )
                            : today,
                        amount: this.amount,
                        missingAmount: this.getMissingAmount(),
                        confirmed: this.confirmed,
                        studentID: this.student.studentID,
                        note: this.note,
                        paymentType: this.paymentType,
                        entreredBY: isUpdating
                            ? this.payment.entreredBY
                            : this.$store.state.authentication.user,
                        updatedOn: isUpdating ? today : null,
                        updatedBY: isUpdating
                            ? this.$store.state.authentication.user
                            : null
                    }
                );

                this.processing = false;

                this.$notify({
                    text: "Payment Saved",
                    group: "notification",
                    type: "success"
                });

                this.closeDialog();

                this.reload();
            } catch (e) {
                this.processing = false;

                const text =
                    this.paymentType == "Credit Card"
                        ? "An error has occurred"
                        : "Payment successfull. Error saving transaction.";

                this.$notify({
                    text: text,
                    group: "notification",
                    type: "error"
                });
            }
        },
        async processCreditCard() {
            const cardInfo = cardValidator.number(this.cardNumber);

            const data = {
                accountID: this.student.familyID,
                appSource: "PURIM BUCHERIM",
                x_Amount: this.amount,
                x_Card_Num: (this.cardNumber || "").replace(/\s/g, ""),
                x_Exp_Date: this.expDate,
                nameOnCard: this.student.fullNameE,
                billingName: this.student.fullNameE,
                cardType: cardInfo.card.niceType,
                paymentType: "CC",
                departmentID: 42,
                paymentDate: moment().format("YYYY-MM-DD HH:mm:ss"),
                saveForFuture: false,
                emailAddressArray: ["no-reply@utaw.org"],
                isTestPayment: this.isTestPayment(),
                paymentCategories: [
                    {
                        Amount: +this.amount,
                        CategoryCode: "PURB"
                    }
                ]
            };

            try {
                await this.$http.post(
                    this.getPaymentDomain() + "/API/payment/purimPayment",
                    data
                );
            } catch (e) {
                throw new Error(e);
            }
        },
        isTestPayment() {
            return window.location.origin.startsWith("http://localhost:8080");
        },
        getPaymentDomain() {
            return "https://utaw.azurewebsites.net";
            // return window.location.origin.startsWith("http://localhost")
            //     ? "https://localhost:44383"
            //     : "https://utaw.azurewebsites.net";
        }
    }
};
</script>
